// askSmart-ui/src/pages/TOSP/useGridSort.tsx
import React, { useState } from "react";

type Direction = "asc" | "desc";

// Column sorting for a TOSP management grid, in the platform's usual style:
// a ▲/▼ triangle in the header, dimmed on the inactive columns; clicking the
// active column flips direction, clicking another starts it ascending.
//
// `valueOf` maps a row + column to the value to compare — numbers sort
// numerically, strings case-insensitively — so each grid states its own sort
// keys and shares everything else.
export function useGridSort<C extends string, T>(
  initialCol: C,
  valueOf: (row: T, col: C) => string | number,
) {
  const [sort, setSort] = useState<{ col: C; dir: Direction }>({
    col: initialCol,
    dir: "asc",
  });

  const toggleSort = (col: C) =>
    setSort((prev) =>
      prev.col === col ? { col, dir: prev.dir === "asc" ? "desc" : "asc" } : { col, dir: "asc" },
    );

  // Rendered inside a grid header cell; carries the header's own typography.
  const sortButton = (col: C, label: string) => (
    <button
      type="button"
      className="tosp-sort-btn"
      onClick={() => toggleSort(col)}
      title={`Sort by ${label}`}
    >
      {label}
      {sort.col !== col ? (
        <span style={{ opacity: 0.3, marginLeft: 4, fontSize: "0.7em" }}>▼</span>
      ) : (
        <span style={{ marginLeft: 4, fontSize: "0.7em" }}>{sort.dir === "asc" ? "▲" : "▼"}</span>
      )}
    </button>
  );

  const sortRows = (rows: T[]): T[] => {
    const factor = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = valueOf(a, sort.col);
      const bv = valueOf(b, sort.col);
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * factor;
      return String(av).localeCompare(String(bv), undefined, { sensitivity: "base" }) * factor;
    });
  };

  return { sort, toggleSort, sortButton, sortRows };
}
